
 import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfileCompletionCard } from "@/components/nursing-homes/owner/dashboard/ProfileCompletionCard";
 import { Calendar, Home, Image, MapPin, Phone, User, Users, Lock, Crown } from "lucide-react";
 import { supabase } from "@/integrations/supabase/client";
 import { useSubscription } from "@/hooks/use-subscription";
 import { Button } from "@/components/ui/button";
 import { useNavigate, Link } from "react-router-dom";
 import { Badge } from "@/components/ui/badge";

type ContextType = { nursingHome: NursingHome };

export default function OverviewPage() {
  const { nursingHome } = useOutletContext<ContextType>();
   const navigate = useNavigate();
   const { subscribed, plan_tier, loading: subscriptionLoading } = useSubscription();
   const [leadCount, setLeadCount] = useState(0);
 
   useEffect(() => {
     fetchLeadCount();
   }, [nursingHome.county, nursingHome.nh_slug]);
 
   const fetchLeadCount = async () => {
     try {
       const countyName = nursingHome.county?.toLowerCase();
       
       // Count direct leads
       const { count: directCount } = await supabase
         .from("nursing_home_leads")
         .select("*", { count: "exact", head: true })
         .eq("nursing_home_id", nursingHome.nh_slug);
 
       // Count waitlist submissions for this county
       const { count: waitlistCount } = await supabase
         .from("waitlist_submissions")
         .select("*", { count: "exact", head: true })
         .contains("preferred_location", [countyName]);
 
       setLeadCount((directCount || 0) + (waitlistCount || 0));
     } catch (error) {
       console.error("Error fetching lead count:", error);
     }
   };

  // Calculate stats for the dashboard
  const contactInfoComplete = !!(
    nursingHome.email && 
    nursingHome.standardized_phone_number
  );
  
  const addressComplete = !!(
    nursingHome.street_name && 
    nursingHome.town && 
    nursingHome.county
  );
  
  const facilitiesCount = (nursingHome.facilities?.length || 0) + 
                          (nursingHome.amenities?.length || 0) + 
                          (nursingHome.services?.length || 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{nursingHome.center_name}</h1>
          <p className="text-muted-foreground">
            {nursingHome.town}, {nursingHome.county}
          </p>
        </div>
        <ProfileCompletionCard nursingHome={nursingHome} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Profile Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Active</div>
            <p className="text-xs text-muted-foreground mt-1">
              Your profile is visible to potential residents
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
             <CardTitle className="text-sm font-medium flex items-center justify-between">
               <span>Active Leads</span>
               {!subscribed && leadCount > 0 && (
                 <Badge variant="secondary" className="text-xs">
                   <Lock className="h-3 w-3 mr-1" />
                   Premium
                 </Badge>
               )}
             </CardTitle>
          </CardHeader>
          <CardContent>
             <div className="text-2xl font-bold">{leadCount}</div>
             {leadCount > 0 ? (
               subscribed ? (
                 <Link 
                   to={`/owner/nursing-homes/${nursingHome.nh_slug}/leads`}
                   className="text-xs text-primary hover:underline mt-1 inline-block"
                 >
                   View lead details →
                 </Link>
               ) : (
                 <Button 
                   size="sm" 
                   variant="link" 
                   className="p-0 h-auto text-xs text-primary"
                   onClick={() => navigate("/owner/subscription")}
                 >
                   <Crown className="h-3 w-3 mr-1" />
                   Unlock to view details
                 </Button>
               )
             ) : (
               <p className="text-xs text-muted-foreground mt-1">
                 No active leads at the moment
               </p>
             )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Features Added</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{facilitiesCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {facilitiesCount === 0 
                ? "No features added yet" 
                : `${facilitiesCount} facilities, amenities & services`}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <h2 className="text-xl font-semibold mt-8">Setup Progress</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className={contactInfoComplete ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${contactInfoComplete ? "bg-green-100" : "bg-amber-100"}`}>
              <Phone className={`h-5 w-5 ${contactInfoComplete ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Contact Information</h3>
              <p className="text-sm text-muted-foreground">
                {contactInfoComplete ? "Complete" : "Incomplete"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={addressComplete ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${addressComplete ? "bg-green-100" : "bg-amber-100"}`}>
              <MapPin className={`h-5 w-5 ${addressComplete ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Address Details</h3>
              <p className="text-sm text-muted-foreground">
                {addressComplete ? "Complete" : "Incomplete"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={facilitiesCount > 0 ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${facilitiesCount > 0 ? "bg-green-100" : "bg-amber-100"}`}>
              <Home className={`h-5 w-5 ${facilitiesCount > 0 ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Facilities & Services</h3>
              <p className="text-sm text-muted-foreground">
                {facilitiesCount > 0 ? `${facilitiesCount} Added` : "None Added"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={nursingHome.main_image_url ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${nursingHome.main_image_url ? "bg-green-100" : "bg-amber-100"}`}>
              <Image className={`h-5 w-5 ${nursingHome.main_image_url ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Photos</h3>
              <p className="text-sm text-muted-foreground">
                {nursingHome.main_image_url ? "Main Photo Added" : "No Photos"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-full p-2 bg-gray-100">
              <User className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium">Staff Management</h3>
              <p className="text-sm text-muted-foreground">Coming Soon</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-full p-2 bg-gray-100">
              <Calendar className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium">Calendar</h3>
              <p className="text-sm text-muted-foreground">Coming Soon</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
